import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const IMG = "https://static.prod-images.emergentagent.com/jobs/8bd93fc7-3267-4a6a-9a52-abdbe32e8bf0/images";

const CATEGORIES = [
  { label: "Aluminium", accent: "#C0C0C0", image: `${IMG}/e6619440d1e425e0b774d559182f0e7c17034e1639c4ef671f01bf4e2fceb7f9.jpeg` },
  { label: "Copper", accent: "#B87333", image: `${IMG}/58b2c17b76e8fdbcf2f8979627e4377205c0b8c6902c010d39740da05bbe8fba.jpeg` },
  { label: "Solar", accent: "#4A90D9", image: `${IMG}/f2738960bbc444da646ac4807e69cc616bcf7ee8b237756799e5760b79b4f6c5.jpeg` },
  { label: "Lithium", accent: "#9BA8B5", image: `${IMG}/6aa1fbe7195f178090791d3e7bd86b861e574b41a5f1c3447b2530b98f3dd1e0.jpeg` },
  { label: "E-Waste", accent: "#5FB878", image: `${IMG}/a5cee7ca6ffcfa783369c24f11b36697025063d360e25e1079f953b930a59a87.jpeg` },
  { label: "Brass", accent: "#C9A227", image: `${IMG}/5a4a75cb6e3ca6195b68aa7f3c140e80a75496aa4b95be7bb9ea21d8ce3e8367.jpeg` },
  { label: "SS", accent: "#C9D2DA", image: `${IMG}/b7b79d500ebb30b605718e14a5449dca42b62e26599af1d56c43f5468e66c2dc.jpeg` },
  { label: "MS", accent: "#7D8590", image: `${IMG}/5ca8286303a2c6bb12a6df9bef42e16691ff91e3f64d5e741fb7bf3d1296a6bc.jpeg` },
];
const N = CATEGORIES.length;

function Slide({ c, i, progress }) {
  const seg = 1 / N;
  const start = i * seg;
  const end = start + seg;
  const f = seg * 0.18;
  const opacity = useTransform(
    progress,
    [start, start + (i === 0 ? 0 : f), end - (i === N - 1 ? 0 : f), end],
    [i === 0 ? 1 : 0, 1, 1, i === N - 1 ? 1 : 0]
  );
  const scale = useTransform(progress, [start, end], [1.18, 1.0]);
  const y = useTransform(progress, [start, start + seg * 0.35], [60, 0]);
  const textOpacity = useTransform(progress, [start + f * 0.5, start + seg * 0.35], [0, 1]);

  return (
    <motion.div style={{ opacity }} className="absolute inset-0" data-testid={`showcase-slide-${c.label.toLowerCase().replace(" ", "-")}`}>
      <motion.img
        src={c.image}
        alt={c.label}
        style={{ scale }}
        className="absolute inset-0 h-full w-full object-cover will-change-transform"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/25 to-[#0A0A0A]/90" />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <motion.p style={{ opacity: textOpacity }} className="font-ticker text-[11px] tracking-[0.5em] uppercase text-white/50 mb-4">
          {String(i + 1).padStart(2, "0")} / {String(N).padStart(2, "0")}
        </motion.p>
        <motion.h3
          style={{ y, opacity: textOpacity, color: c.accent }}
          className="font-display font-black uppercase tracking-tighter leading-none text-[15vw] sm:text-[11vw] will-change-transform"
        >
          {c.label}
        </motion.h3>
        <motion.a
          href="#products"
          style={{ opacity: textOpacity }}
          data-testid={`showcase-link-${c.label.toLowerCase().replace(" ", "-")}`}
          className="mt-8 inline-flex items-center gap-2 border border-white/25 bg-[#0A0A0A]/40 backdrop-blur px-6 py-3 font-ticker text-[10px] tracking-[0.3em] uppercase text-white/85 hover:border-[#25D366] hover:text-[#25D366] transition-colors duration-300"
        >
          View Products <ArrowUpRight size={13} />
        </motion.a>
      </div>
    </motion.div>
  );
}

export default function CategoryShowcase() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const slideVh = typeof window !== "undefined" && window.innerWidth < 768 ? 42 : 60;

  return (
    <div ref={ref} className="relative border-b border-white/10" style={{ height: `${N * slideVh}vh` }} data-testid="category-showcase">
      <div className="sticky top-0 h-screen overflow-hidden">
        {CATEGORIES.map((c, i) => (
          <Slide key={c.label} c={c} i={i} progress={scrollYProgress} />
        ))}
      </div>
    </div>
  );
}
